import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { ContentWrapper } from '../components/content_wrapper/content_wrapper';
import { ScrollToTop } from '../components/scroll_to_top/scroll_to_top';
import { getServerEnv, ServerEnvs } from '../env';
import { useQueryString } from '../hooks/use_query_string';
import { apiV1 } from '../lib/api';
import { StorageKeys } from '../lib/local_storage';
import { AuthActions } from '../redux/auth';
import { getExperimentUuid } from '../redux/user';

const env = getServerEnv();

const getAuthUrl = () => {
  if (env === ServerEnvs.PRODUCTION) {
    return 'https://auth.omniafishing.com/oauth2/authorize';
  } else {
    return 'https://omniafishing-dev.auth.us-east-2.amazoncognito.com/oauth2/authorize';
  }
};

const getRedirectUrl = () => {
  if (env === ServerEnvs.LOCAL) {
    return 'http://localhost:8080/oauth';
  } else if (env === ServerEnvs.STAGE) {
    return 'https://stage.omniafishing.com/oauth';
  }
  return 'https://www.omniafishing.com/oauth';
};

const getClientId = () => {
  if (env === ServerEnvs.PRODUCTION) {
    return '4r9igr4rct05l2qr9focl3qhjf';
  } else {
    return 'mpf1neofhsokb5pven7oiv6a4';
  }
};

export const buildAuthUrl = (params: {
  provider: 'Google' | 'SignInWithApple';
  codeChallenge: string;
  state: string;
}) => {
  const { provider, codeChallenge, state } = params;
  const baseParamsString = '?response_type=code&scope=openid+email';
  const baseUrl = getAuthUrl() + baseParamsString;
  const redirectUrl = getRedirectUrl();
  const clientId = getClientId();
  return `${baseUrl}&client_id=${clientId}&identity_provider=${provider}&redirect_uri=${redirectUrl}&code_challenge=${codeChallenge}&code_challenge_method=S256&state=${state}`;
};

export const generateCodeChallenge = async (verifier: string) => {
  // 1. Encode the verifier string to a Uint8Array
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);

  // 2. Hash the data using SHA-256
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);

  // 3. Base64-URL encode the hash buffer
  // (This is a common and reliable way to do it in JS)
  const base64 = btoa(String.fromCharCode.apply(null, new Uint8Array(hashBuffer)));
  const base64Url = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

  return base64Url;
};

const generateRandomCode = () => {
  const randomBytes = window.crypto.getRandomValues(new Uint8Array(32)); // 32 bytes = 256 bits
  // Base64-URL encode the random bytes
  const verifier = btoa(String.fromCharCode.apply(null, randomBytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return verifier;
};

const getCodeVerifier = () => {
  return sessionStorage.getItem(StorageKeys.oauthCodeVerifier);
};

const getOauthState = () => {
  return sessionStorage.getItem(StorageKeys.oauthState);
};

interface OauthState {
  fromPath?: string;
  token: string;
}

const encodeOauthState = (stateObj: OauthState) => {
  return btoa(JSON.stringify(stateObj));
};
const decodeOauthState = (encodedState: string): OauthState | null => {
  try {
    const decoded = atob(encodedState);
    return JSON.parse(decoded);
  } catch (e) {
    console.error('Failed to decode OAuth state:', e);
    return null;
  }
};

export const generateAndSaveCodeVerifier = () => {
  const codeVerifier = generateRandomCode();
  sessionStorage.setItem(StorageKeys.oauthCodeVerifier, codeVerifier);
  return codeVerifier;
};

export const generateAndSaveState = (params: { fromPath?: string }) => {
  const { fromPath } = params || {};
  const stateToken = generateRandomCode();
  const state: OauthState = {
    fromPath: fromPath || '/',
    token: stateToken,
  };
  const encodedState = encodeOauthState(state);
  sessionStorage.setItem(StorageKeys.oauthState, encodedState);
  return encodedState;
};

type AuthProvider = 'Google' | 'SignInWithApple' | 'Omnia';

export const saveLastUsedAuth = (provider: AuthProvider) => {
  localStorage.setItem(StorageKeys.authLastUsed, provider);
};

export const getLastUsedAuth = (): AuthProvider | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage.getItem(StorageKeys.authLastUsed) as AuthProvider;
};

export const OauthPage = () => {
  const { getCurrentQuery } = useQueryString();
  const currentQuery = getCurrentQuery<{
    error_description: string;
    code: string;
    state: string;
  }>();
  const { code, error_description, state } = currentQuery;
  const stateDecoded = decodeOauthState(state);
  const stateToken = stateDecoded?.token;
  const stateFromPath = stateDecoded?.fromPath || '/';
  const experimentUuid = useSelector(getExperimentUuid);
  const dispatch = useDispatch();
  const history = useHistory();
  const called = useRef(false);

  useEffect(() => {
    const codeVerifier = getCodeVerifier();
    const savedOauthState = getOauthState();
    const savedOauthStateDecoded = decodeOauthState(savedOauthState);
    const savedOauthStateToken = savedOauthStateDecoded?.token;

    if (code && !called.current && stateToken === savedOauthStateToken) {
      called.current = true;
      apiV1
        .oauthLogin({
          code,
          experiment_uuid: experimentUuid,
          redirect_url: getRedirectUrl(),
          code_verifier: codeVerifier,
        })
        .then((res) => {
          history.replace(stateFromPath);
          dispatch(AuthActions.AUTH_LOGIN_SUCCESS(res.data));
        });
    }
  }, [code]);

  return (
    <ScrollToTop>
      <section className="p-12">
        <ContentWrapper>
          {error_description ? (
            <>
              <h1 className="text-center text-4xl mb-3">Error logging in</h1>
              <p className="text-center text-base">Please try again or contact support.</p>
            </>
          ) : (
            <>
              <h1 className="text-center text-4xl">Finishing Login...</h1>
            </>
          )}
        </ContentWrapper>
      </section>
    </ScrollToTop>
  );
};
export default OauthPage;
